import { useState } from "react";
import { useReveal } from "../lib/useReveal";
import { ChevronDown } from "lucide-react";
import { CRISIS_HELPLINES } from "../data/crisisResources";

function CrisisFaqAnswer() {
    return (
        <div className="space-y-4">
            <p>
                Genesis Recovery is supportive, but it is not an emergency service. If you or someone
                you love is in crisis right now, please reach out:
            </p>
            <ul className="space-y-2.5">
                {CRISIS_HELPLINES.map((line) => (
                    <li key={line.tel} className="flex items-start gap-2.5">
                        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                        <span>
                            <a
                                href={`tel:${line.tel}`}
                                className="font-semibold text-foreground hover:text-primary transition-colors"
                            >
                                {line.number}
                            </a>
                            {" — "}
                            {line.label}
                            {line.detail ? ` (${line.detail})` : ""}
                        </span>
                    </li>
                ))}
            </ul>
            <p>
                You don&apos;t have to wait until you &apos;have it together&apos; to ask for help.
            </p>
        </div>
    );
}

const FAQS = [
    {
        q: "Is Genesis Recovery free?",
        a: "Yes — the core Genesis Recovery app including Hope AI companion, daily check-ins, private journaling, and the treatment directory is free to download and use. Optional mentor connections and premium features may be introduced later; you'll always be in control.",
    },
    {
        q: "Is my data really private?",
        a: "Yes. Your conversations with Hope, journals, and progress are fully encrypted in transit and at rest. We don't sell data, we don't share with insurers or employers, and there are no public profiles. You can export or delete everything anytime.",
    },
    {
        q: "Is Hope a licensed therapist?",
        a: "No. Hope is an AI companion trained on evidence-based approaches (CBT, motivational interviewing, mindfulness) — designed to support reflection, encouragement, and daily rituals. Hope is not a replacement for a licensed therapist, and will encourage users to seek appropriate professional support when needed. Hope is not a substitute for therapy, medical care, or treatment for substance use disorder. For detox, withdrawal, or co-occurring mental health conditions, please work with a licensed clinician.",
    },
    {
        q: "How does the treatment directory work?",
        a: "We curate verified listings — sober living homes, rehabs, outpatient programs, and community resources — and let you filter by state, program type, and keyword. You can browse the directory without creating an account. All listings are informational only and are not endorsements.",
    },
    {
        q: "What if I'm in crisis?",
        content: <CrisisFaqAnswer />,
    },
    {
        q: "Can I use Genesis if I'm not sober yet?",
        a: "Absolutely. Genesis is for anyone on a recovery journey — whether you're thinking about getting sober, in your first week, or ten years in. There's no judgment about where you are today.",
    },
];

function FaqItem({ item, index, open, onToggle }) {
    return (
        <div
            data-testid={`faq-item-${index}`}
            className="bg-card border rounded-xl overflow-hidden"
        >
            <button
                type="button"
                aria-expanded={open}
                onClick={onToggle}
                className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-muted/40 transition-colors"
            >
                <span className="font-semibold text-foreground">{item.q}</span>
                <ChevronDown
                    className={`w-5 h-5 text-muted-foreground shrink-0 transition-transform ${
                        open ? "rotate-180" : ""
                    }`}
                />
            </button>
            {open && (
                <div className="px-5 pb-5 text-muted-foreground leading-relaxed">
                    {item.content ?? item.a}
                </div>
            )}
        </div>
    );
}

export default function Faq() {
    const ref = useReveal();
    const [openIdx, setOpenIdx] = useState(0);

    return (
        <section
            ref={ref}
            data-testid="faq-section"
            id="faq"
            className="py-20 md:py-24 bg-muted/30 border-y"
        >
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-12 reveal">
                    <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 tracking-tight">
                        Frequently asked questions
                    </h2>
                    <p className="text-lg text-muted-foreground">
                        Still have questions?{" "}
                        <a href="#contact" className="text-primary font-medium hover:underline">
                            Get in touch
                        </a>
                        .
                    </p>
                </div>
                <div className="space-y-3 reveal">
                    {FAQS.map((item, i) => (
                        <FaqItem
                            key={item.q}
                            item={item}
                            index={i}
                            open={openIdx === i}
                            onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
}
